/** Client events for ad pixels / tag managers (FR-REG-09). No PII in either payload. */
import type { RegisterResponse } from '@ffs/shared';

export type RegisteredSource = Pick<
  RegisterResponse,
  'form_id' | 'webinar_slug' | 'session_start_at'
>;

export interface RegisteredDetail {
  form_id: string;
  webinar_slug: string;
  session_start_at: number;
  session_start_iso: string;
}

export interface DataLayerEvent {
  event: 'ffs_registered';
  ffs_form_id: string;
  ffs_webinar_slug: string;
  ffs_session_start_iso: string;
}

export function registeredPayloads(res: RegisteredSource): {
  detail: RegisteredDetail;
  dataLayer: DataLayerEvent;
} {
  const iso = new Date(res.session_start_at).toISOString();
  return {
    detail: {
      form_id: res.form_id,
      webinar_slug: res.webinar_slug,
      session_start_at: res.session_start_at,
      session_start_iso: iso,
    },
    dataLayer: {
      event: 'ffs_registered',
      ffs_form_id: res.form_id,
      ffs_webinar_slug: res.webinar_slug,
      ffs_session_start_iso: iso,
    },
  };
}

type WindowLike = {
  dispatchEvent(ev: Event): boolean;
  dataLayer?: { push(ev: unknown): unknown };
};

/** Dispatches `ffs:registered` on `window` and pushes to `window.dataLayer` when present. */
export function fireRegistered(res: RegisteredSource, win: WindowLike = window): void {
  const { detail, dataLayer } = registeredPayloads(res);
  try {
    win.dispatchEvent(new CustomEvent('ffs:registered', { detail }));
  } catch {
    // Never let a host-page listener error break the confirmation.
  }
  try {
    if (win.dataLayer && typeof win.dataLayer.push === 'function') win.dataLayer.push(dataLayer);
  } catch {
    // Same: GTM errors are the host page's problem.
  }
}

/**
 * Dispatches `ffs:email_step` (and `ffs_email_step` to the dataLayer) when a two-step form moves
 * past its email step. No PII: the email itself is never included.
 */
export function fireEmailStep(formId: string, webinarSlug: string, win: WindowLike = window): void {
  try {
    win.dispatchEvent(
      new CustomEvent('ffs:email_step', { detail: { form_id: formId, webinar_slug: webinarSlug } }),
    );
  } catch {
    // Host-page listener errors must not block the second step.
  }
  try {
    if (win.dataLayer && typeof win.dataLayer.push === 'function')
      win.dataLayer.push({
        event: 'ffs_email_step',
        ffs_form_id: formId,
        ffs_webinar_slug: webinarSlug,
      });
  } catch {
    // Same: GTM errors are the host page's problem.
  }
}
